import { Component, EventEmitter, Output, OnInit, Input, Inject } from '@angular/core';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { SolutionParticipationService } from '../../service/solution-participation.service';
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import { SPLocationTotalSolutionGroupsExpandedData, SPLocationExpandedSolutionGroup, SPLocationExpandedProduct } from '../../types/solution-participation-types';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';

@Component({
    selector: 'app-location-total-solutions-grid',
    templateUrl: './location-total-solutions-grid.component.html',
    styleUrls: ['./location-total-solutions-grid.component.scss'],
    standalone: false
})
export class LocationTotalSolutionsGridComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Input() siteId = '';
  @Input() siteName = '';
  @Input() totalSolutionGroups: number = 0;
  @Input() siteData: SPLocationTotalSolutionGroupsExpandedData | undefined;

  filters: ContractModel | undefined;
  filtersSubscription: Subscription | undefined;
  siteDataSubscription: Subscription | undefined;
  widgetType: string = 'all';

  isLoading: boolean = false;
  noSolutionsPurchased: boolean = false;
  selectedSolutionGroup: any = null;
  selectedSolutionGroupData: SPLocationExpandedSolutionGroup | undefined;
  productData: SPLocationExpandedProduct[] | undefined;
  selectedRowKey: string = '';
  purchasedSolutionGroupRows: SPLocationExpandedSolutionGroup[][] = [];
  notPurchasedSolutionGroupRows: SPLocationExpandedSolutionGroup[][] = [];
  // Placeholder count for the Neptune skeleton loader grid
  skeletonCards: number[] = [1, 2, 3, 4, 5, 6];

  get isRefreshedSolutionParticipationUIEnabled(): boolean {
    return this._featureFlags.enableRefreshedSolutionParticipationUI;
  }

  constructor(
    private _filterService: FiltersService,
    private _solutionParticipationService: SolutionParticipationService,
    private _mixpanelService: MixpanelService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags
  ) { }

  async ngOnInit() : Promise<void> {
    // Disables scroll on the main solution participation page
    document.body.style.overflow = 'hidden';

    // Subscribe to filters
    this.filtersSubscription = this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);

    this.setSolutionGroupRows();

    // Set up subscription to radio button
    this._solutionParticipationService.selectedRadioButtonObservable$.subscribe((widgetType) => {
      this.widgetType = widgetType;
    });

    // Mixpanel tracking
    this._mixpanelService.track('Opened Total Purchased Solution Groups Modal', {
      division: this.filters?.division,
      dataView: this.filters?.dataView,
      regionSelected: this.filters?.regions,
      countriesSelected: this.filters?.countries,
      H5s: this.filters?.topLevelContractGroup,
      H7s: this.filters?.secondLevelContractFilter,
      acctsSelected: this.filters?.accts,
      periodSelected: this.filters?.period,
      "Custom Date Range": this.filters?.dateRange,
      siteId: this.siteId,
      siteName: this.siteName,
      totalSolutionGroups: this.totalSolutionGroups,
      widgetType: this.widgetType
    });

    this.isLoading = false;
  }

  public closeModal() {
    // Re-enable body level scroll
    document.body.style.overflow = 'auto';
    this.close.emit();
  }

  public toggleExpand(item: any, rowKey: string) {
    if (this.isCardSelected(item)) {
      // If the same card is clicked, toggle it off
      this.selectedSolutionGroup = null;
      this.selectedSolutionGroupData = undefined;
      this.productData = undefined;
      this.selectedRowKey = '';
    } else {
      // A different card is selected
      this.selectedSolutionGroup = item;
      this.selectedRowKey = rowKey;
      this.selectedSolutionGroupData = this.siteData?.solutionGroups.find((group) => group.solutionGroup === this.selectedSolutionGroup);
      this.productData = this.selectedSolutionGroupData?.products;

      // Trigger mixpanel event
      this._mixpanelService.track('Opened Solution Group with Products Modal from Total Solution Groups Modal', {
        division: this.filters?.division,
        dataView: this.filters?.dataView,
        regionSelected: this.filters?.regions,
        countriesSelected: this.filters?.countries,
        H5s: this.filters?.topLevelContractGroup,
        H7s: this.filters?.secondLevelContractFilter,
        acctsSelected: this.filters?.accts,
        periodSelected: this.filters?.period,
        "Custom Date Range": this.filters?.dateRange,
        siteId: this.siteId,
        siteName: this.siteName,
        solutionGroup: this.selectedSolutionGroup
      });
    }
  }

  public isCardSelected(item: any) {
    return this.selectedSolutionGroup === item;
  }

  public setSolutionGroupRows() {
    const allSolutionGroups = this.siteData?.solutionGroups ?? [];
    const purchasedSolutionGroups = allSolutionGroups.filter(group => group.isPurchased);
    const notPurchasedSolutionGroups = allSolutionGroups.filter(group => !group.isPurchased);

    this.purchasedSolutionGroupRows = this.chunkSolutionGroups(purchasedSolutionGroups);
    this.notPurchasedSolutionGroupRows = this.chunkSolutionGroups(notPurchasedSolutionGroups);

    this.noSolutionsPurchased = allSolutionGroups.length === 0;
  }

  public getRowKey(section: 'purchased' | 'notPurchased', rowIndex: number): string {
    return `${section}-${rowIndex}`;
  }

  public get shouldShowNoLocationsMessage(): boolean {
    return !this.selectedSolutionGroupData?.isPurchased && (this.selectedSolutionGroupData?.countOfProducts ?? 0) === 0;
  }

  private chunkSolutionGroups(solutionGroups: SPLocationExpandedSolutionGroup[]): SPLocationExpandedSolutionGroup[][] {
    const rows: SPLocationExpandedSolutionGroup[][] = [];
    for (let i = 0; i < solutionGroups.length; i += 3) {
      rows.push(solutionGroups.slice(i, i + 3));
    }
    return rows;
  }

  ngOnDestroy() {
    if (this.filtersSubscription) {
      this.filtersSubscription.unsubscribe();
    }
    // Re-enable body level scroll
    document.body.style.overflow = 'auto';
  }
}
